import React from "react";
import { connect } from "react-redux";
import language from "../../language";
import { Table } from "../Table";
import {
  deleteInventory,
  fetchInventories,
  getInventoryDashboardCounts,
} from "../../utils/queries/production/inventoryQuery";
import { DeleteModal, Modal } from "../Modal";
import { defineRole } from "../../utils/handleAccessRoles";
import { getStorage } from "../../utils/storage";
import icons from "../../constants/icons";
import ViewInventory from "./ViewInventory";
import PrintableLabel from "../Production/ComponentLabeling/printableLabel";
import exportPDF from "../../utils/exportPDF";
import CardCount from "../CardCount/CardCount";
import {
  BloodGroupFilter,
  ProductFilter,
// eslint-disable-next-line no-unused-vars
} from "../Filters";
import { CSVLink } from "react-csv";
import { ROLE_INVENTORY } from "../../constants/ROLES";
import handleFilters from "../../utils/handleFilters";

class Inventories extends React.Component {
// eslint-disable-next-line no-unused-vars
  csvDownload = React.createRef();

  state = {
    isLoading: false,
    data: [],
    page: 1,
    limit: 10,
    search_text: "",
    selectedData: {},
    delete_value: "",
    error: {},
    csvData: [],
    user: {},
    suspendReason: "",
    counts: {
      labelled: 0,
      transformed: 0,
      dispatched: 0,
      pending: 0,
      returned: 0,
      returnsSent: 0,
      transferred: 0,
      received: 0,
      archived: 0,
      incoming: 0,
    },
    isDisabled: true,
    filters: {
      // startDate: moment()
      //   .startOf("D")
      //   .subtract(30, "days")
      //   .format("YYYY-MM-DD"),
      // endDate: moment().endOf("D").format("YYYY-MM-DD"),
    },
  };

  componentDidMount = async () => {
    const user = await getStorage();
    await this.setState({ user });

    this.fetchData(true, this.state.filters);
  };

  fetchData = async (isLoading, search = {}) => {
    const user = await getStorage();
    const updatedFilters = {
      ...this.state.filters,
      ...search,
      status: this.props.status,
      // Virtual stock tab: expiry is evaluated per request, so the tab needs
      // no status of its own and nothing has to be moved onto the rows.
      onlyExpired: this.props.onlyExpired,
      // Keeps the table under the cards consistent with them. Only the
      // Inventory tab passes this; the component is reused on screens that
      // must still show a product whatever its expiry, such as a donation's
      // own product list.
      onlyNotExpired: this.props.excludeExpired,
      // Lower bound on expiry, for a tab that wants only recent expiries
      // rather than every unit that has ever lapsed. Expired Products uses
      // it; the rest leave it undefined and the allowlist drops it.
      startExpiredDate:
        search.startExpiredDate || this.props.startExpiredDate,
    };

    const formattedFilters = await this.returnFilters(updatedFilters);

    this.setState({ filters: formattedFilters });

    // Pin the list to what this facility holds right now. The default
    // scoping matches stock OR sourceStock, so a facility also sees units it
    // dispatched to somebody else - fine for a stock list, wrong for Expired
    // Products, where a lapsed unit is the problem of whoever has it in the
    // fridge. Admin accounts carry no stock and so still see every facility.
    //
    // Applied after returnFilters because handleFilters is an allowlist and
    // exactStock is not on it; the cards reach getData directly for the same
    // reason.
    this.getData(isLoading, {
      ...formattedFilters,
      exactStock: this.props.ownStockOnly ? user.stock : undefined,
    });

    this.getDashboardCountsData(isLoading, {
      ...formattedFilters,
      stock: user.stock,
    });
  };

  onChangeText(field, e) {
    let { error } = this.state;

    delete error[field];

    this.setState({
      [field]: e && e?.target ? e?.target?.value : e,
      error,
    });
  }

  returnFilters = async (search = {}) => {
    const { page, limit } = this.state;

    const filters = await handleFilters({
      page,
      limit,
      donation: this.props.donationId,
      id: this.props.inventoryId,
      ...search,
    });

    return filters;
  };

  getData = async (isLoading, search = {}) => {
    try {
      const { defaultLanguage } = this.props;

      this.setState({ isLoading });

      delete search.endDate;

      const { data, count, totalRecords } = await fetchInventories(defaultLanguage, {
        ...search,
        limit: search.limit || 10,
        allowStatusWithDonation: this.props.allowStatusWithDonation,
      });

      this.setState({
        data: data,
        totalPageCount: count,
        totalRecords,
        isLoading: false,
        page: search.page || 1,
        limit: search.limit || 10,
      });
    } catch (error) {
      this.setState({
        isLoading: false,
      });
    }
  };

  getDashboardCountsData = async (isLoadingCounts, search = {}) => {
    try {
      const { defaultLanguage } = this.props;

      this.setState({ isLoadingCounts });

      const filters = await this.returnFilters();
      const params = {
        ...filters,
        ...search,
      };

      const counts = await getInventoryDashboardCounts(defaultLanguage, params);

      this.setState({
        counts: {
          ...this.state.counts,
          pending: counts?.pending || 0,
          incoming: counts?.incoming || 0,
          labelled: counts?.labelled || 0,
          transformed: counts?.transformed || 0,
          transferred: counts?.transferred || 0,
          received: counts?.received || 0,
          returned: counts?.returned || 0,
          returnsSent: counts?.returnsSent || 0,
          archived: counts?.archived || 0,
        },
        isLoadingCounts: false,
      });
    } catch (error) {
      this.setState({
        isLoadingCounts: false,
      });
    }
  };

  handlePagination(page, _tab, limit) {
    this.setState(
      {
        page,
        ...(limit != null && { limit }),
      },
      () => {
        this.getData(true, {
          ...this.state.filters,
          page,
          limit: this.state.limit || 10,
        });
      }
    );
  }

  handleLimitChange = (newLimit) => {
    this.setState({ limit: newLimit, page: 1 }, () => {
      this.getData(true, {
        ...this.state.filters,
        page: 1,
        limit: newLimit,
      });
    });
  };

  handleShowModal(modal, modalTitle, selectedData = {}) {
    this.setState({
      [modal]: true,
      modalTitle: modalTitle,
      selectedData,
      reason: selectedData.reason || "",
    });
  }

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  returnTableHeaders() {
    let headers = [
      {
        title: language[this.props.defaultLanguage].donation_number,
        key: "donation.donationNumber",
        type: "link",
        onPress: (item) => {
          if (this.props.showActualLabel) {
            this.handleShowModal(
              "showLabelModal",
              item?.donation?.donationNumber || item.code,
              item
            );
            return;
          }

          this.handleShowModal(
            "showModal",
            item?.donation?.donationNumber || item.code,
            item
          );
        },
      },
      {
        title: language[this.props.defaultLanguage].product_type,
        key: "component.productType.name",
      },
      {
        title: language[this.props.defaultLanguage].code,
        key: "code",
      },
      {
        title: language[this.props.defaultLanguage].product,
        key: "name",
      },
      {
        title: language[this.props.defaultLanguage].blood_group,
        key: "bloodGroup",
      },
      {
        title: language[this.props.defaultLanguage].volume,
        key: "volume",
      },
      {
        title: language[this.props.defaultLanguage].concentration,
        key: "concentrationValue",
      },
      {
        title: language[this.props.defaultLanguage].phlebotomy_date,
        key: "donation.collectionDate",
        formatTime: "ll",
        isMoment: true,
      },
      {
        title: language[this.props.defaultLanguage].expired_date,
        key: "expiredDate",
        formatTime: "lll",
        isMoment: true,
      },

      {
        title: language[this.props.defaultLanguage].labelled_by,
        key: "verifiedBy.pin",
      },
      {
        title: language[this.props.defaultLanguage].status,
        key: "status",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].source,
        render: (item) => item?.fromService?.name || item?.sourceStock?.name || "-",
      },
      {
        title: language[this.props.defaultLanguage].current_stock,
        key: "stock.name",
      },

      {
        title: language[this.props.defaultLanguage].reason,
        key: "reasonId.label",
      },
      {
        title: language[this.props.defaultLanguage].shipment_status,
        key: "shipmentInfo.status",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].transferred_date,
        key: "shipmentInfo.date",
        formatTime: "lll",
        isMoment: true,
      },
      {
        title: language[this.props.defaultLanguage].transferred_by,
        key: "shipmentInfo.user.pin",
        styles: {
          textTransform: "uppercase",
        },
      },
      {
        title: language[this.props.defaultLanguage].reception,
        key: "receptionInfo.status",
        type: "status",
        styles: {
          textTransform: "uppercase",
        },
      },
      {
        title: language[this.props.defaultLanguage].received_by,
        key: "receptionInfo.user.pin",
        type: "status",
        styles: {
          textTransform: "uppercase",
        },
      },
      {
        title: language[this.props.defaultLanguage].reception_date,
        key: "shipmentInfo.date",
        formatTime: "lll",
        isMoment: true,
      },
      {
        title: language[this.props.defaultLanguage].inventory_status,
        key: "inventoryStatus",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].actions,
        key: "action",
      },
    ];

    return headers;
  }

  onDelete = async () => {
    try {
      this.setState({ isDeleting: true });

      await deleteInventory(
        this.props.defaultLanguage,
        this.state.selectedData._id
      );

      this.setState({ isDeleting: false });

      this.getData(true);
      this.handleCloseModal("showDeleteModal");
    } catch (error) {
      this.setState({
        isDeleting: false,
      });
    }
  };

  downloadExcel = async () => {
    try {
      this.setState({
        isLoading: true,
      });

      const { defaultLanguage } = this.props;

      const filters = await this.returnFilters(this.state.filters);

      const { data } = await fetchInventories(defaultLanguage, filters);

      this.setState({
        isLoading: false,
        csvData: data.map((el) => {
          return {
            ...el,
            center: el?.center?.name,
            centerSite: el?.centerSite?.name,
          };
        }),
      });

      this.csvDownload.current?.link.click();
    } catch (error) {
      this.setState({
        isLoading: false,
      });
    }
  };

  downloadPDF = async () => {
    this.setState({
      isLoading: true,
    });

    const { defaultLanguage } = this.props;

    const filters = await this.returnFilters(this.state.filters);

    const { data } = await fetchInventories(defaultLanguage, filters);

    this.setState({
      isLoading: false,
    });

    exportPDF("Inventories", this.returnTableHeaders(), data);
  };

  handleRemoteSearch = async (search) => {
    await new Promise((r) => this.setState({ searching: true, page: 1 }, r));
    await this.fetchData(true, {
      q: search && search !== "" ? search : undefined,
      page: 1,
    });
    this.setState({ searching: false });
  };

  handleSearch = async (e) => {
    const search = e?.target?.value;
    this.setState({ search_text: search });
  };

  render() {
    return (
      <>
        <div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <ProductFilter
                handleApplyFilters={(isLoading, filters) =>
                  this.fetchData(isLoading, filters)
                }
              />

              <BloodGroupFilter
                handleApplyFilters={(isLoading, filters) =>
                  this.fetchData(isLoading, filters)
                }
              />
              {/* <DateFilter
                handleApplyFilters={(isLoading, filters) =>
                  this.fetchData(isLoading, filters)
                }
                startDate={this.state.startDate}
                endDate={this.state.endDate}
              /> */}
            </div>
          </div>
          {/* Every card counts live, transfusable stock, so none of them
              describes the Virtual stock tab — showing them there would put
              figures next to a table they do not describe. */}
          {!this.props.onlyExpired && (
          <div className="row mb-3">
            {/* Sent to us, not yet accepted. Falls to zero as reception is
                recorded, at which point the units appear under In stock. */}
            <div className="col-6 col-md-3">
              <CardCount
                count={this.state.counts.incoming}
                title={language[this.props.defaultLanguage].awaiting_reception}
                color="#078ECE"
                icon="print"
                isLoading={this.state.isLoadingCounts}
                onPress={() => {
                  this.getData(true, {
                    ...this.state.filters,
                    page: 1,
                    status: undefined,
                    awaitingReceptionFor: this.state.user.stock,
                    excludeRejectedPending: true,
                    onlyNotExpired: true,
                    countStatusParams: {
                      "shipmentInfo.status": "transferred",
                      "receptionInfo.status": "incoming",
                    },
                  });
                }}
              />
            </div>

            <div className="col-6 col-md-3">
              <CardCount
                count={this.state.counts.pending}
                title={language[this.props.defaultLanguage].pending_to_be_labelled}
                color="orange"
                icon="schedule"
                isLoading={this.state.isLoadingCounts}
                onPress={() =>
                  this.getData(true, {
                    ...this.state.filters,
                    page: 1,
                    exactStock: this.state.user.stock,
                    status: "pending",
                    onlyNotExpired: true,
                  })
                }
              />
            </div>

            {/* What is physically held right now. Same definition as the
                Dashboard tab's per-blood-group cards, so the two agree. */}
            <div className="col-6 col-md-3">
              <CardCount
                count={this.state.counts.received}
                title={language[this.props.defaultLanguage].in_stock}
                color="green"
                icon="check_circle"
                isLoading={this.state.isLoadingCounts}
                onPress={() => {
                  this.getData(true, {
                    ...this.state.filters,
                    page: 1,
                    status: undefined,
                    exactStock: this.state.user.stock,
                    inventoryStatus: "INSTOCK",
                    excludeIncomingReception: true,
                    onlyNotExpired: true,
                  });
                }}
              />
            </div>

            {/* Sent by us and not yet accepted. Rises on dispatch, falls the
                moment the receiving entity records reception. */}
            <div className="col-6 col-md-3">
              <CardCount
                count={this.state.counts.transferred}
                title={language[this.props.defaultLanguage].in_transit}
                color="purple"
                icon="local_shipping"
                isLoading={this.state.isLoadingCounts}
                onPress={() => {
                  this.getData(true, {
                    ...this.state.filters,
                    page: 1,
                    status: undefined,
                    stock: undefined,
                    sourceStock: this.state.user.stock,
                    onlyNotExpired: true,
                    countStatusParams: {
                      "shipmentInfo.status": "transferred",
                      "receptionInfo.status": "incoming",
                    },
                  });
                }}
              />
            </div>
          </div>
          )}

          {/* Both return cards keep expired units on purpose: expiry is itself
              a reason to send a product back, so filtering them out would hide
              the returns most worth seeing. Neither is bounded to today —
              these are running totals an entity has to account for, not a
              day's activity. */}
          {!this.props.onlyExpired && (
          <div className="row mb-3">
            {/* Sent back TO us by someone we supplied. A return swaps the two
                stock fields, so after it `stock` names the receiver. */}
            <div className="col-6 col-md-3">
              <CardCount
                count={this.state.counts.returned}
                title={language[this.props.defaultLanguage].returns_received}
                color="#078ECE"
                icon="keyboard_return"
                isLoading={this.state.isLoadingCounts}
                onPress={() => {
                  this.getData(true, {
                    ...this.state.filters,
                    page: 1,
                    status: undefined,
                    exactStock: this.state.user.stock,
                    onlyNotExpired: undefined,
                    countStatusParams: {
                      "shipmentInfo.status": "returned",
                    },
                  });
                }}
              />
            </div>

            {/* Sent back BY us to whoever supplied the unit — after the return
                `sourceStock` names the sender, which is us. */}
            <div className="col-6 col-md-3">
              <CardCount
                count={this.state.counts.returnsSent}
                title={language[this.props.defaultLanguage].returns_sent}
                color="#8E44AD"
                icon="undo"
                isLoading={this.state.isLoadingCounts}
                onPress={() => {
                  this.getData(true, {
                    ...this.state.filters,
                    page: 1,
                    status: undefined,
                    stock: undefined,
                    sourceStock: this.state.user.stock,
                    onlyNotExpired: undefined,
                    countStatusParams: {
                      "shipmentInfo.status": "returned",
                    },
                  });
                }}
              />
            </div>
          </div>
          )}
          {/* <div className="col-md-3">
            <CardCount
              count={this.state.counts.received}
              title={language[this.props.defaultLanguage].received}
              color="#078ECE"
              icon="print"
              isLoading={this.state.isLoadingCounts}
              onPress={() => {
                this.getData(true, {
                  ...this.state.filters,
                  status: "labelled",
                  page: 1,
                  countStatusParams: {
                    "shipmentInfo.status": "pending",
                  },
                });
              }}
            />
          </div> */}
        </div>

        <Table
          data={this.state.data}
          isSearch
          page={this.state.page}
          limit={this.state.limit}
          addSearchButton
          search_text={this.state.search_text}
          searching={this.state.searching}
          handleSearch={(e) => this.onChangeText("search_text", e)}
          handlePressSearch={() =>
            this.handleRemoteSearch(this.state.search_text)
          }
          totalPageCount={this.state.totalPageCount}
          totalRecords={this.state.totalRecords}
          isLoading={this.state.isLoading}
          handlePagination={this.handlePagination.bind(this)}
          onLimitChange={this.handleLimitChange}
          headers={this.returnTableHeaders()}
          filters={[
            {
              type: "button",
              title: language[this.props.defaultLanguage].refresh,
              button_type: "button",
              icon: icons.refresh,
              onPress: (item) => this.fetchData(true),
            },
            // {
            //   type: "dropdown",
            //   filterName: "date_range",
            //   title: language[this.props.defaultLanguage].date_range,
            //   button_type: "dropdown",
            //   handleApplyFilters: () => this.getData(true),
            //   startDate: moment().startOf("D").format("YYYY-MM-DD"),
            //   endDate: moment().endOf("D").format("YYYY-MM-DD"),
            // },
            {
              type: "export",
              title: language[this.props.defaultLanguage].export_,
              button_type: "dropdown",
              icon: "bx bxs-download",
              options: [
                {
                  name: "PDF",
                  onPress: this.downloadPDF.bind(this),
                },
                {
                  name: "CSV",
                  onPress: this.downloadExcel.bind(this),
                },
              ],
            },
          ]}
          actions={[
            {
              canAccess: defineRole({
                roles: this.state.user.accessRole,
                menu: ROLE_INVENTORY,
                operation: "read",
              }),
              name: language[this.props.defaultLanguage].view,
              icon: icons.eye_on,
              onPress: (item) => {
                if (this.props.showActualLabel) {
                  this.handleShowModal(
                    "showLabelModal",
                    item?.donation?.donationNumber || item.code,
                    item
                  );
                  return;
                }

                this.handleShowModal(
                  "showModal",
                  item?.donation?.donationNumber || item.code,
                  item
                );
              },
            },
          ]}
        />
        <Modal
          handleClose={this.handleCloseModal.bind(this, "showModal")}
          show={this.state.showModal}
          title={this.state.modalTitle}
          showHeaderBottomBorder={false}
          size="lg"
        >
          <ViewInventory
            handleCloseModal={this.handleCloseModal.bind(this, "showModal")}
            getData={this.fetchData.bind(this)}
            {...this.state.selectedData}
            donationId={
              this.props.donationId || this.state?.selectedData?.donation?._id
            }
            disabled={this.state.isDisabled}
          />
        </Modal>

        <Modal
          handleClose={this.handleCloseModal.bind(this, "showDeleteModal")}
          show={this.state.showDeleteModal}
          title={this.state.modalTitle}
        >
          <DeleteModal
            handleCloseModal={this.handleCloseModal.bind(
              this,
              "showDeleteModal"
            )}
            error={this.state.error.delete_password}
            value={this.state.delete_value}
            onDelete={this.onDelete.bind(this)}
            onChange={(e) => this.onChangeText("delete_value", e)}
            isDeleting={this.state.isDeleting}
            name={this.state.selectedData._id}
            getData={this.getData.bind(this)}
          />
        </Modal>
        <Modal
          handleClose={this.handleCloseModal.bind(this, "showLabelModal")}
          show={this.state.showLabelModal}
          title={this.state.modalTitle}
          showHeaderBottomBorder={false}
        >
          <PrintableLabel
            handleCloseModal={this.handleCloseModal.bind(
              this,
              "showLabelModal"
            )}
            productId={this.state?.selectedData?._id}
          />
        </Modal>
        <CSVLink
          ref={this.csvDownload}
          filename={"Inventories" + new Date().getTime()}
          data={this.state.csvData}
        ></CSVLink>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  const { filters } = state.Filters;
  return {
    defaultLanguage,
    filters,
  };
};

export default connect(mapStateToProps)(Inventories);
